import React, { useEffect, useState } from 'react';
import { Gift, Lock, Star, Check, Loader2, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { rewardsAPI, qrAPI } from '../../services/api';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Progress } from '../../components/ui/progress';

const Rewards = () => {
  const { user } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReward, setSelectedReward] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [generatingQR, setGeneratingQR] = useState(false);

  useEffect(() => {
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    try {
      const response = await rewardsAPI.getAll();
      setRewards(response.data.rewards || []);
    } catch (error) {
      console.error('Failed to fetch rewards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemClick = async (reward) => {
    if (!reward.can_redeem) {
      toast.error(`You need ${reward.points_required - (user?.current_points || 0)} more points`);
      return;
    }
    
    setSelectedReward(reward);
    setGeneratingQR(true);
    
    try {
      const response = await qrAPI.getRewardQR(user.id, reward.id);
      setQrData(response.data);
    } catch (error) {
      toast.error('Failed to generate redemption QR code');
      setSelectedReward(null);
    } finally {
      setGeneratingQR(false);
    }
  };

  const closeModal = () => {
    setSelectedReward(null);
    setQrData(null);
  };

  const getProgressPercent = (required) => {
    const current = user?.current_points || 0;
    return Math.min((current / required) * 100, 100);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      Service: '💇‍♀️',
      Discount: '🏷️',
      Credit: '💳',
      Product: '🎁',
    };
    return icons[category] || '🎁';
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <div className="skeleton h-20 rounded-2xl" />
        <div className="grid md:grid-cols-2 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="skeleton h-40 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  const availableRewards = rewards.filter(r => r.can_redeem);
  const lockedRewards = rewards.filter(r => !r.can_redeem);

  return (
    <div className="p-4 md:p-6 space-y-6 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl text-[#4A154B] mb-2">Rewards</h1>
        <p className="text-gray-600">Redeem your points for exclusive perks</p>
      </div>

      {/* Points Balance */}
      <div className="glass rounded-2xl p-5 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">Your Balance</p>
          <p className="font-serif text-3xl text-[#4A154B]">{user?.current_points || 0} <span className="text-lg">points</span></p>
        </div>
        <div className="w-14 h-14 rounded-full gradient-primary flex items-center justify-center">
          <Star className="w-7 h-7 text-white" />
        </div>
      </div>

      {/* Available Rewards */}
      {availableRewards.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-serif text-xl text-[#4A154B] flex items-center gap-2">
            <Check className="w-5 h-5 text-[#8FBC8F]" />
            Available to Redeem
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {availableRewards.map((reward) => (
              <div
                key={reward.id}
                className="reward-card cursor-pointer"
                onClick={() => handleRedeemClick(reward)}
                data-testid={`reward-available-${reward.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="text-3xl">{getCategoryIcon(reward.category)}</div>
                  <div className="flex-1">
                    <h3 className="font-serif text-lg text-[#4A154B] mb-1">{reward.name}</h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{reward.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-[#C9A689] font-semibold">{reward.points_required} pts</span>
                      <span className="text-sm text-[#8FBC8F] font-medium flex items-center gap-1">
                        <Check className="w-4 h-4" /> Ready to redeem
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Locked Rewards */}
      {lockedRewards.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-serif text-xl text-[#4A154B] flex items-center gap-2">
            <Lock className="w-5 h-5 text-gray-400" />
            Keep Earning
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {lockedRewards.map((reward) => (
              <div
                key={reward.id}
                className="reward-card locked"
                data-testid={`reward-locked-${reward.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="text-3xl opacity-50">{getCategoryIcon(reward.category)}</div>
                  <div className="flex-1">
                    <h3 className="font-serif text-lg text-[#4A154B] mb-1">{reward.name}</h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{reward.description}</p>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">{user?.current_points || 0} / {reward.points_required} pts</span>
                        <span className="text-[#C9A689]">{reward.points_required - (user?.current_points || 0)} more needed</span>
                      </div>
                      <Progress value={getProgressPercent(reward.points_required)} className="h-2" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {rewards.length === 0 && (
        <div className="text-center py-12">
          <Gift className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No rewards available at the moment</p>
        </div>
      )}

      {/* Redemption QR Modal */}
      <Dialog open={!!selectedReward} onOpenChange={closeModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#4A154B] text-center">
              Redeem Reward
            </DialogTitle>
          </DialogHeader>
          
          {generatingQR ? (
            <div className="py-12 text-center">
              <Loader2 className="w-12 h-12 text-[#C9A689] animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Generating QR code...</p>
            </div>
          ) : qrData ? (
            <div className="text-center space-y-4">
              <p className="text-gray-600 mb-4">Show this QR code to staff to redeem your reward</p>
              
              <div className="qr-container mx-auto inline-block animate-pulse-glow">
                <QRCode
                  value={JSON.stringify(qrData)}
                  size={200}
                  level="H"
                  fgColor="#4A154B"
                />
              </div>
              
              <div className="bg-[#F5E6D3] rounded-xl p-4">
                <h4 className="font-serif text-lg text-[#4A154B]">{selectedReward?.name}</h4>
                <p className="text-sm text-gray-600">{selectedReward?.points_required} points</p>
              </div>
              
              <p className="text-xs text-gray-400">
                This QR code expires in 5 minutes and can only be used once.
              </p>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Rewards;
